import { CircleCheck, CircleX } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Logo } from '@/components/brand/Logo';
import { LoadingState } from '@/components/ui';
import { isCheckKind, useCertificateCheck } from '@/features/certificates/api';
import { isApiError } from '@/lib/api';
import { clinicDayLabel, periodLabel } from '@/pages/ops/mc-format';
import styles from '@/pages/ops/mc.module.css';
import parity from '@/pages/ops/parity.module.css';

type Banner = { tone: 'ok' | 'bad'; title: string; body: string };

/**
 * Public QR check (prototype showVerify) for every document kind the API
 * checks (RL-1132): MC, time slip, referral and imaging letters, vaccination
 * certificate and outside prescription. Reference, valid or revoked, issuing
 * clinic and issue date (an MC also its dates); never the patient, the
 * doctor, a diagnosis, the content or the revoke reason. A kind this page
 * does not know is titled "Document", never as an MC.
 */
export function VerifyPage() {
  const { t } = useTranslation();
  const { code = '' } = useParams();
  const q = useCertificateCheck(code);
  useEffect(() => {
    document.title = t('verify.pageTitle');
  }, [t]);
  const c = q.data;
  const err = q.error && isApiError(q.error) ? q.error : null;
  const unknown = !!err && (err.status === 404 || err.status === 400);
  const kind = c && isCheckKind(c.kind) ? c.kind : 'other';
  const doc = t(`verify.kinds.${kind}.noun`);

  let banner: Banner | null = null;
  if (q.isLoading) {
    // The loading state is shown instead.
  } else if (unknown)
    banner = {
      tone: 'bad',
      title: t('verify.unknown'),
      body: t('verify.unknownBody'),
    };
  else if (err?.status === 429)
    banner = {
      tone: 'bad',
      title: t('verify.tooMany'),
      body: t('verify.tooManyBody'),
    };
  else if (q.error || !c)
    banner = {
      tone: 'bad',
      title: t('verify.unavailable'),
      body: t('verify.unavailableBody'),
    };
  else if (c.status === 'valid')
    banner = {
      tone: 'ok',
      title: t('verify.valid', { doc }),
      body: t('verify.validBody', { clinic: c.branch.name }),
    };
  else if (c.status === 'revoked')
    banner = {
      tone: 'bad',
      title: c.revokedAt
        ? t('verify.revokedOn', { date: clinicDayLabel(c.revokedAt) })
        : t('verify.revoked'),
      body: t('verify.revokedBody', { doc }),
    };
  // Any other status is never shown as valid.
  else
    banner = {
      tone: 'bad',
      title: t('verify.notConfirmed'),
      body: t('verify.notConfirmedBody'),
    };

  return (
    <main className={styles.vpage}>
      <div className={styles.vwrap}>
        <div className={styles.vtop}>
          <Logo />
          <span>{t('verify.heading')}</span>
        </div>
        {q.isLoading ? (
          <LoadingState />
        ) : (
          banner && (
            <div
              className={`${styles.vban} ${banner.tone === 'ok' ? styles.ok : styles.bad}`}
              role={banner.tone === 'ok' ? 'status' : 'alert'}
            >
              {banner.tone === 'ok' ? (
                <CircleCheck aria-hidden="true" />
              ) : (
                <CircleX aria-hidden="true" />
              )}
              <div>
                <b>{banner.title}</b>
                <span>{banner.body}</span>
              </div>
            </div>
          )
        )}
        {c && !q.error && (
          <div className={styles.doc}>
            <div className={styles.title}>
              <b>{t(`verify.kinds.${kind}.ms`)}</b>
              <span>{t(`verify.kinds.${kind}.en`)}</span>
              <span className={styles.mono}>{c.reference}</span>
            </div>
            <dl className={parity.kv}>
              <dt>{t('verify.clinic')}</dt>
              <dd>
                {c.branch.name}
                {c.branch.address && (
                  <span className={parity.sub}>
                    {c.branch.address.join(', ')}
                  </span>
                )}
              </dd>
              <dt>{t('verify.issued')}</dt>
              <dd>{clinicDayLabel(c.issuedAt)}</dd>
              {kind === 'medical_certificate' && (
                <>
                  <dt>{t('certificates.period')}</dt>
                  <dd>
                    {periodLabel({ from: c.from ?? null, to: c.to ?? null })}
                    {c.days != null &&
                      ` · ${t('certificates.days', { count: c.days })}`}
                  </dd>
                </>
              )}
            </dl>
          </div>
        )}
        <p className={styles.vfoot}>{t('verify.footer')}</p>
      </div>
    </main>
  );
}
